import { useEffect, useState } from 'react';

export const useBrowserCompatibilityData = () => {
  const [userAgent, setUserAgent] = useState<string>('');
  const [supportsNativeSmoothScroll, setSupportsNativeSmoothScroll] = useState<boolean>(false);

  // Sets variables once DOM has mounted to avoid SSR issues
  useEffect(() => {
    setUserAgent(window.navigator.userAgent ?? '');
    setSupportsNativeSmoothScroll('scrollBehavior' in document.documentElement.style);
  }, []);

  const isOpendoorAppEvoWebView = userAgent.indexOf('EvolvedWebView') > -1;
  const isOpendoorAppWebView =
    userAgent.indexOf('OpendoorAppWebView') > -1 && !isOpendoorAppEvoWebView;
  const isWebView = isOpendoorAppWebView || isOpendoorAppEvoWebView;
  return {
    userAgent,
    /** True if the user is using chrome */
    IS_USING_CHROME: userAgent.includes('Chrome'),
    /**
     * Updated User Agent that OpendoorAppWebView/EvolvedWebView/${DeviceVersion}
     * This will not break any existing webview checks
     */
    isOpendoorAppEvoWebView,
    isOpendoorAppWebView,
    isWebView,
    /**
     * Whether the browser supports smooth scrolling on elements
     * See more here: https://caniuse.com/?search=scrollTo
     */
    supportsNativeSmoothScroll,
  };
};

/**
 * Checks that a value is not null nor undefined.
 *
 * Can be used for filtering null and undefined values.
 * ```ts
 * const x: (number | null)[]
 * const y: number[] = x.filter(hasValue)
 * ```
 */
export const hasValue = <T>(value: T | null | undefined): value is T => {
  return value !== null && value !== undefined;
};

type NonNullableTuple<T extends readonly unknown[]> = {
  [K in keyof T]: NonNullable<T[K]>;
};

/**
 * If all values are present (non-null, non-undefined), applies the callback
 * and returns its result. Otherwise returns the default value.
 *
 * ```ts
 * const a: string | null;
 * const b: number | undefined;
 *
 * // Without callback — returns narrowed tuple or false
 * const checked = mapPresentValues([a, b]);
 * if (checked) { const [x, y] = checked; } // x: string, y: number
 *
 * // With callback and default — never returns false
 * const safe = mapPresentValues([a, b], (x, y) => x.length + y, 0);
 * // safe: number
 * ```
 */
export function mapPresentValues<const T extends readonly unknown[]>(
  values: readonly [...T],
): NonNullableTuple<T> | false;
export function mapPresentValues<const T extends readonly unknown[], R>(
  values: readonly [...T],
  fn: (...args: NonNullableTuple<T>) => R,
  defaultValue: R,
): R;
export function mapPresentValues<const T extends readonly unknown[], R>(
  values: readonly [...T],
  fn?: (...args: NonNullableTuple<T>) => R,
  defaultValue?: R,
): NonNullableTuple<T> | R | false {
  if (values.some((v) => v === null || v === undefined)) {
    return defaultValue !== undefined ? defaultValue : false;
  }
  const narrowed = values as unknown as NonNullableTuple<T>;
  return fn ? fn(...narrowed) : narrowed;
}

/**
 * Returns true if all of the provided values are not null nor undefined.
 */
export const allHaveValue = (...values: unknown[]): boolean => {
  return values.every((v) => v !== null && v !== undefined);
};

/** A cost's dollar amount as a percentage of a base price, rounded to one decimal place. */
export const percentageOfSalesPrice = (costCents: number, salesPriceCents: number): number =>
  Math.round((costCents / salesPriceCents) * 1000) / 10;

type Entry<T> = { [K in keyof T]: [K, T[K]] }[keyof T];

/**
 * Type-safe version of Object.entries that preserves key types
 * @public
 */
export const typedEntries = Object.entries as <T extends object>(obj: T) => Entry<T>[];

/**
 * Type-safe version of Object.keys that preserves key types
 * @public
 */
export const typedKeys = <T extends object>(obj: T): (keyof T)[] => Object.keys(obj) as (keyof T)[];

/**
 * Type-safe version of Object.values that preserves value types
 * @public
 */
export const typedValues = <T extends object>(obj: T): T[keyof T][] =>
  Object.values(obj) as T[keyof T][];
